import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Coins } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshCoins } = useAuth();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | success | failed
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      if (attempts.current > 30) {
        setStatus("failed");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("success");
          await refreshCoins();
          return;
        }
        if (["expired", "failed", "refunded"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (error) {
        if (error.response?.status === 401) {
          setStatus("failed");
          return;
        }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, refreshCoins]);

  return (
    <div className="max-w-md mx-auto px-6 py-24 text-center animate-fade-up" data-testid="payment-result">
      {status === "checking" && (
        <>
          <Loader2 size={48} className="animate-spin text-primary mx-auto mb-6" />
          <h1 className="font-display text-3xl">Confirming your payment...</h1>
          <p className="text-muted-foreground mt-2">This only takes a moment.</p>
        </>
      )}
      {status === "success" && (
        <>
          <CheckCircle2 size={56} className="text-emerald-400 mx-auto mb-6" />
          <h1 className="font-display text-4xl">Coins added</h1>
          <p className="text-muted-foreground mt-2 flex items-center justify-center gap-1">
            <Coins size={16} className="text-gold" /> Your wallet is topped up and ready.
          </p>
          <div className="flex gap-3 justify-center mt-8">
            <Link to="/" data-testid="success-browse" className="px-5 py-2.5 rounded-md bg-primary hover:bg-primary/90 transition-colors duration-300">Browse stories</Link>
            <Link to="/wallet" className="px-5 py-2.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors duration-300">View wallet</Link>
          </div>
        </>
      )}
      {status === "failed" && (
        <>
          <XCircle size={56} className="text-primary mx-auto mb-6" />
          <h1 className="font-display text-3xl">Payment not completed</h1>
          <p className="text-muted-foreground mt-2">Your payment could not be confirmed. No coins were charged.</p>
          <Link to="/wallet" className="inline-block mt-8 px-5 py-2.5 rounded-md bg-primary hover:bg-primary/90 transition-colors duration-300">Back to wallet</Link>
        </>
      )}
    </div>
  );
}
